export const LOGIN_VERIFICATION_TTL_MS = 5 * 60 * 1000;
export const LOGIN_RESEND_COOLDOWN_MS = 60 * 1000;

const STORAGE_KEY = "muzone.auth.loginChallenge.v1";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const hasWindow = () => typeof window !== "undefined";

const normalizeChallengeId = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    if (!UUID_PATTERN.test(trimmed)) {
        return null;
    }

    return trimmed.toLowerCase();
};

const readStorage = () => {
    if (!hasWindow()) {
        return null;
    }

    try {
        return window.sessionStorage.getItem(STORAGE_KEY);
    } catch (_) {
        return null;
    }
};

const writeStorage = (value) => {
    if (!hasWindow()) {
        return;
    }

    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (_) {
        // Ignore storage failures and keep the flow in-memory only.
    }
};

export const clearPendingLoginChallenge = () => {
    if (!hasWindow()) {
        return;
    }

    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {
        // Ignore storage failures.
    }
};

export const persistPendingLoginChallenge = (challengeId, timing = Date.now()) => {
    const normalizedChallengeId = normalizeChallengeId(challengeId);
    const issuedAt = typeof timing === "number"
        ? timing
        : Number.isFinite(timing?.issuedAt)
            ? timing.issuedAt
            : Date.now();
    const expiresAt = typeof timing === "object" && Number.isFinite(timing?.expiresAt)
        ? timing.expiresAt
        : issuedAt + LOGIN_VERIFICATION_TTL_MS;
    const resendAvailableAt = typeof timing === "object" && Number.isFinite(timing?.resendAvailableAt)
        ? timing.resendAvailableAt
        : issuedAt + LOGIN_RESEND_COOLDOWN_MS;

    if (
        !normalizedChallengeId
        || !Number.isFinite(issuedAt)
        || !Number.isFinite(expiresAt)
        || !Number.isFinite(resendAvailableAt)
        || expiresAt < issuedAt
        || resendAvailableAt < issuedAt
    ) {
        clearPendingLoginChallenge();
        return null;
    }

    const activeChallenge = readPendingLoginChallenge(issuedAt);
    if (
        activeChallenge
        && activeChallenge.challengeId === normalizedChallengeId
        && typeof timing !== "object"
    ) {
        return activeChallenge;
    }

    const nextValue = {
        challengeId: normalizedChallengeId,
        issuedAt,
        expiresAt,
        resendAvailableAt,
    };

    writeStorage(nextValue);
    return nextValue;
};

export const readPendingLoginChallenge = (now = Date.now()) => {
    const raw = readStorage();
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw);
        const challengeId = normalizeChallengeId(parsed?.challengeId);
        const issuedAt = Number(parsed?.issuedAt);
        const expiresAt = Number(parsed?.expiresAt);
        const resendAvailableAt = Number.isFinite(Number(parsed?.resendAvailableAt))
            ? Number(parsed?.resendAvailableAt)
            : issuedAt + LOGIN_RESEND_COOLDOWN_MS;

        if (
            !challengeId
            || !Number.isFinite(issuedAt)
            || !Number.isFinite(expiresAt)
            || !Number.isFinite(resendAvailableAt)
            || expiresAt < issuedAt
            || resendAvailableAt < issuedAt
            || expiresAt <= now
        ) {
            clearPendingLoginChallenge();
            return null;
        }

        return {
            challengeId,
            issuedAt,
            expiresAt,
            resendAvailableAt,
        };
    } catch (_) {
        clearPendingLoginChallenge();
        return null;
    }
};
